import styled from 'styled-components'
import { NODE_DEFAULTS } from '../../../../utils/nodeDefaults'
import type { ReactFlowNodeData } from '../../../../types/mindmap-components'

/** A single action rendered in the edge-anchored Action Dock. */
export interface DockAction {
  id: string
  icon: React.ComponentType<{ size: number; color?: string; strokeWidth?: number }>
  label: string
  /** Subtitle shown beneath label in dropdown items. */
  subtitle?: string
  onClick: (e: React.MouseEvent) => void
  group: 'branch' | 'prototype' | 'validate'
  disabled?: boolean
  disabledReason?: string
  loading?: boolean
  /** When present, this item becomes a group trigger — click expands a popover with these sub-actions rendered side-by-side. */
  children?: DockAction[]
  /** When present, this item renders as a single button that opens a dropdown menu with these items. */
  dropdownChildren?: DockAction[]
}

export interface BaseNodeProps {
  id: string
  nodeData: ReactFlowNodeData
  icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
  categoryLabel: string
  categorySubheading?: string // Short explanatory text shown below the category label
  categoryDescription?: string // Tooltip text explaining what this node type is for
  renderNodeContent?: (currentText: string) => React.ReactNode
  borderLayers?: string[]
  hideDefaultContent?: boolean // If true, hides the default label/description textarea
  renderHeaderActions?: () => React.ReactNode // Optional custom buttons/actions in header
  hideSourceHandle?: boolean // If true, hides the source handle (+ button) for creating child nodes
  onSourceHandleClick?: () => void // Optional override for the source handle click
  borderColorOverride?: string // Optional color override for node border (e.g., from tags)
  hasDirectTag?: boolean // If true, the node has a tag directly applied (not inherited)
  renderLeftHandle?: () => React.ReactNode // Optional left handle for adding parent nodes
  renderAboveNode?: () => React.ReactNode // Optional content rendered above the node (e.g., action buttons)
  onChatReady?: (api: { autoGenerate: () => void }) => void // Callback when chat mounts, exposes chat API
  dockActions?: DockAction[] // Actions to render in the edge-anchored Action Dock (replaces above-node CTAs + handle)
  prototypeActions?: DockAction[] // Prototype sub-actions for the card footer dropdown (IdeaNode only)
}

// Reusable styled textarea for node text fields
export const NodeTextArea = styled.textarea`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: inherit;
  font-weight: inherit;
  text-align: left;
  outline: none;
  resize: none;
  overflow: hidden;
  width: 100%;
  max-width: ${NODE_DEFAULTS.maxWidth - 32}px;
  font-family: inherit;
  line-height: 1.3;
  padding: 0;
  margin: 0;
  word-wrap: break-word;
  white-space: pre-wrap;
`
